import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Button, FormControl, FormLabel, Grid, Input } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { compareObjects } from '../../Utilities';
import OrgApiService from '../../../api/OrgApiService';
import SaveButton from '../../Common/SaveButton';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

const PermissionLevels = {
    None: 0,
    ViewOnly: 1,
    Edit: 2,
};

const organizationApiService = new OrgApiService();

export default function EditOrganizationModal (props) {
    const {
        open,
        currentOrganization,
        onCancel,
        onSave,
    } = props;

    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [organizationName, setOrganizationName] = useState('');
    const [domain, setDomain] = useState('');
    const [active, setActive] = useState(0);
    const [otherOrgsPermissionLevel, setOtherOrgsPermissionLevel] = useState(0);
    const [canSearch, setCanSearch] = useState(0);
    const [externalUsers, setExternalUsers] = useState(0);
    const [ownReported, setOwnReported] = useState(0);
    const [assignedTo, setAssignedTo] = useState(0);
    const [riskManagementModule, setRiskManagementModule] = useState(false);
    const [locationProjectPermission, setLocationProjectPermission] = useState(PermissionLevels.None);
    const [organizationPermission, setOrganizationPermission] = useState(PermissionLevels.None);
    const [priorityPermission, setPriorityPermission] = useState(PermissionLevels.None);
    const [statusPermission, setStatusPermission] = useState(PermissionLevels.None);
    const [assignedToPermission, setAssignedToPermission] = useState(PermissionLevels.None);
    const [tagsPermission, setTagsPermission] = useState(PermissionLevels.None);
    const [duePermission, setDuePermission] = useState(PermissionLevels.None);
    const [isoClausePermission, setIsoClausePermission] = useState(PermissionLevels.None);
    const [auditorPermission, setAuditorPermission] = useState(PermissionLevels.None);
    const [deptPermission, setDeptPermission] = useState(PermissionLevels.None);
    const [processPermission, setProcessPermission] = useState(PermissionLevels.None);
    const [jobPermission, setJobPermission] = useState(PermissionLevels.None);
    const [taskPermission, setTaskPermission] = useState(PermissionLevels.None);
    const [incidentDescriptionPermission, setIncidentDescriptionPermission] = useState(PermissionLevels.None);
    const [requirementPermission, setRequirementPermission] = useState(PermissionLevels.None);
    const [findingPermission, setFindingPermission] = useState(PermissionLevels.None);
    const [equipmentPermission, setEquipmentPermission] = useState(PermissionLevels.None);
    const [riskSourceDescriptionPermission, setRiskSourceDescriptionPermission] = useState(PermissionLevels.None);
    const [proposedAdditionalControlPermission, setProposedAdditionalControlPermission] = useState(PermissionLevels.None);
    const [evidencePermission, setEvidencePermission] = useState(PermissionLevels.None);
    const [correctionPermission, setCorrectionPermission] = useState(PermissionLevels.None);
    const [causePermission, setCausePermission] = useState(PermissionLevels.None);
    const [correctiveActionPermission, setCorrectiveActionPermission] = useState(PermissionLevels.None);
    const [verifierPermission, setVerifierPermission] = useState(PermissionLevels.None);
    const [verificationEvidencePermission, setVerificationEvidencePermission] = useState(PermissionLevels.None);
    const [verificationDatePermission, setVerificationDatePermission] = useState(PermissionLevels.None);
    const [deleteTasks, setDeleteTasks] = useState(0);
    const [editDeletePosts, setEditDeletePosts] = useState(0);
    const [mergeTasks, setMergeTasks] = useState(0);
    const [massEditTasks, setMassEditTasks] = useState(0);
    const [useReports, setUseReports] = useState(0);
    const [createEditReports, setCreateEditReports] = useState(0);
    const [viewTasksTime, setViewTasksTime] = useState(0);
    const [editTasksTime, setEditTasksTime] = useState(0);
    const [assignInternal, setAssignInternal] = useState(0);

    const permissionLabel = (level) => {
        switch (level) {
            case PermissionLevels.None: return t('permissionLevel.none');
            case PermissionLevels.ViewOnly: return t('permissionLevel.viewOnly');
            case PermissionLevels.Edit: return t('permissionLevel.edit');
            default: return '';
        }
    };

    const columns = useMemo(() => [
        {
            field: 'fieldPermission',
            headerName: t('editOrg.grid.fieldPermissionHeader'),
            flex: 1,
        },
        {
            field: 'ogFieldPermission',
            headerName: t('editOrg.grid.permissionsHeader'),
            type: 'singleSelect',
            editable: true,
            flex: 2,
            valueOptions: [
                { value: PermissionLevels.None, label: t('permissionLevel.none') },
                { value: PermissionLevels.ViewOnly, label: t('permissionLevel.viewOnly') },
                { value: PermissionLevels.Edit, label: t('permissionLevel.edit') },
            ],
            renderCell: (params) => permissionLabel(params.row.ogFieldPermission),
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t]);

    const rows = [
        { rowId: 1, fieldPermission: t('fieldName.locationProject'), ogFieldPermission: locationProjectPermission, setPermissionValue: setLocationProjectPermission },
        { rowId: 2, fieldPermission: t('fieldName.organization'), ogFieldPermission: organizationPermission, setPermissionValue: setOrganizationPermission },
        { rowId: 3, fieldPermission: t('fieldName.priority'), ogFieldPermission: priorityPermission, setPermissionValue: setPriorityPermission },
        { rowId: 4, fieldPermission: t('fieldName.status'), ogFieldPermission: statusPermission, setPermissionValue: setStatusPermission },
        { rowId: 5, fieldPermission: t('fieldName.assignedTo'), ogFieldPermission: assignedToPermission, setPermissionValue: setAssignedToPermission },
        { rowId: 7, fieldPermission: t('fieldName.tags'), ogFieldPermission: tagsPermission, setPermissionValue: setTagsPermission },
        { rowId: 8, fieldPermission: t('fieldName.due'), ogFieldPermission: duePermission, setPermissionValue: setDuePermission },
        { rowId: 9, fieldPermission: t('fieldName.isoClause'), ogFieldPermission: isoClausePermission, setPermissionValue: setIsoClausePermission },
        { rowId: 10, fieldPermission: t('fieldName.auditor'), ogFieldPermission: auditorPermission, setPermissionValue: setAuditorPermission },
        { rowId: 11, fieldPermission: t('fieldName.dept'), ogFieldPermission: deptPermission, setPermissionValue: setDeptPermission },
        { rowId: 12, fieldPermission: t('fieldName.process'), ogFieldPermission: processPermission, setPermissionValue: setProcessPermission },
        { rowId: 13, fieldPermission: t('fieldName.job'), ogFieldPermission: jobPermission, setPermissionValue: setJobPermission },
        { rowId: 14, fieldPermission: t('fieldName.task'), ogFieldPermission: taskPermission, setPermissionValue: setTaskPermission },
        { rowId: 15, fieldPermission: t('fieldName.incidentDescription'), ogFieldPermission: incidentDescriptionPermission, setPermissionValue: setIncidentDescriptionPermission },
        { rowId: 16, fieldPermission: t('fieldName.requirement'), ogFieldPermission: requirementPermission, setPermissionValue: setRequirementPermission },
        { rowId: 17, fieldPermission: t('fieldName.finding'), ogFieldPermission: findingPermission, setPermissionValue: setFindingPermission },
        { rowId: 18, fieldPermission: t('fieldName.equipment'), ogFieldPermission: equipmentPermission, setPermissionValue: setEquipmentPermission },
        { rowId: 19, fieldPermission: t('fieldName.riskSourceDescription'), ogFieldPermission: riskSourceDescriptionPermission, setPermissionValue: setRiskSourceDescriptionPermission },
        { rowId: 20, fieldPermission: t('fieldName.proposedAdditionalControl'), ogFieldPermission: proposedAdditionalControlPermission, setPermissionValue: setProposedAdditionalControlPermission },
        { rowId: 21, fieldPermission: t('fieldName.evidence'), ogFieldPermission: evidencePermission, setPermissionValue: setEvidencePermission },
        { rowId: 22, fieldPermission: t('fieldName.correction'), ogFieldPermission: correctionPermission, setPermissionValue: setCorrectionPermission },
        { rowId: 23, fieldPermission: t('fieldName.cause'), ogFieldPermission: causePermission, setPermissionValue: setCausePermission },
        { rowId: 24, fieldPermission: t('fieldName.correctiveAction'), ogFieldPermission: correctiveActionPermission, setPermissionValue: setCorrectiveActionPermission },
        { rowId: 25, fieldPermission: t('fieldName.verifier'), ogFieldPermission: verifierPermission, setPermissionValue: setVerifierPermission },
        { rowId: 26, fieldPermission: t('fieldName.verificationEvidence'), ogFieldPermission: verificationEvidencePermission, setPermissionValue: setVerificationEvidencePermission },
        { rowId: 27, fieldPermission: t('fieldName.verificationDate'), ogFieldPermission: verificationDatePermission, setPermissionValue: setVerificationDatePermission },
    ];

    useEffect(() => {
        if (open) {
            setOrganizationName(currentOrganization?.ogName || '');
            setDomain(currentOrganization?.ogDomain || '');
            setActive(currentOrganization?.ogActive || 0);
            setOtherOrgsPermissionLevel(currentOrganization?.ogOtherOrgsPermissionLevel || 0);
            setCanSearch(currentOrganization?.ogCanSearch || 0);
            setExternalUsers(currentOrganization?.ogExternalUser || 0);
            setOwnReported(currentOrganization?.ogCanOnlySeeOwnReported || 0);
            setAssignedTo(currentOrganization?.ogCanBeAssignedTo || 0);
            setRiskManagementModule(currentOrganization?.ogEnableRiskManagement || false);
            setLocationProjectPermission(currentOrganization?.ogProjectFieldPermissionLevel || PermissionLevels.None);
            setOrganizationPermission(currentOrganization?.ogOrgFieldPermissionLevel || PermissionLevels.None);
            setPriorityPermission(currentOrganization?.ogPriorityFieldPermissionLevel || PermissionLevels.None);
            setStatusPermission(currentOrganization?.ogStatusFieldPermissionLevel || PermissionLevels.None);
            setAssignedToPermission(currentOrganization?.ogAssignedToFieldPermissionLevel || PermissionLevels.None);
            setTagsPermission(currentOrganization?.ogTagsFieldPermissionLevel || PermissionLevels.None);
            setDuePermission(currentOrganization?.ogDueDateFieldPermissionLevel || PermissionLevels.None);
            setIsoClausePermission(currentOrganization?.ogIsoClauseFieldPermissionLevel || PermissionLevels.None);
            setAuditorPermission(currentOrganization?.ogAuditorFieldPermissionLevel || PermissionLevels.None);
            setDeptPermission(currentOrganization?.ogDeptFieldPermissionLevel || PermissionLevels.None);
            setProcessPermission(currentOrganization?.ogProcessFieldPermissionLevel || PermissionLevels.None);
            setJobPermission(currentOrganization?.ogJobFieldPermissionLevel || PermissionLevels.None);
            setTaskPermission(currentOrganization?.ogTaskFieldPermissionLevel || PermissionLevels.None);
            setIncidentDescriptionPermission(currentOrganization?.ogIncidentDescriptionFieldPermissionLevel || PermissionLevels.None);
            setRequirementPermission(currentOrganization?.ogRequirementFieldPermissionLevel || PermissionLevels.None);
            setFindingPermission(currentOrganization?.ogFindingFieldPermissionLevel || PermissionLevels.None);
            setEquipmentPermission(currentOrganization?.ogEquipmentFieldPermissionLevel || PermissionLevels.None);
            setRiskSourceDescriptionPermission(currentOrganization?.ogRiskSourceDescriptionFieldPermissionLevel || PermissionLevels.None);
            setProposedAdditionalControlPermission(currentOrganization?.ogProposedAdditionalControlFieldPermissionLevel || PermissionLevels.None);
            setEvidencePermission(currentOrganization?.ogEvidenceFieldPermissionLevel || PermissionLevels.None);
            setCorrectionPermission(currentOrganization?.ogCorrectionFieldPermissionLevel || PermissionLevels.None);
            setCausePermission(currentOrganization?.ogCauseFieldPermissionLevel || PermissionLevels.None);
            setCorrectiveActionPermission(currentOrganization?.ogCorrectiveActionFieldPermissionLevel || PermissionLevels.None);
            setVerifierPermission(currentOrganization?.ogVerifierFieldPermissionLevel || PermissionLevels.None);
            setVerificationEvidencePermission(currentOrganization?.ogVerificationEvidenceFieldPermissionLevel || PermissionLevels.None);
            setVerificationDatePermission(currentOrganization?.ogVerificationDateFieldPermissionLevel || PermissionLevels.None);
            setDeleteTasks(currentOrganization?.ogCanDeleteBug || 0);
            setEditDeletePosts(currentOrganization?.ogCanEditAndDeletePosts || 0);
            setMergeTasks(currentOrganization?.ogCanMergeBugs || 0);
            setMassEditTasks(currentOrganization?.ogCanMassEditBugs || 0);
            setUseReports(currentOrganization?.ogCanUseReports || 0);
            setCreateEditReports(currentOrganization?.ogCanEditReports || 0);
            setViewTasksTime(currentOrganization?.ogCanViewTasks || 0);
            setEditTasksTime(currentOrganization?.ogCanEditTasks || 0);
            setAssignInternal(currentOrganization?.ogCanAssignToInternalUsers || 0);
        }
    }, [open, currentOrganization]);

    function handleCancel () {
        onCancel();
    }

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        let newOrganization = getUpdatedOrganization();

        if (!organizationName) {
            snackbar.error(t('editOrg.validation.nameRequired'));
            return;
        }

        if (!domain) {
            snackbar.error(t('editOrg.validation.domainRequired'));
            return;
        }

        if (!currentOrganization) {
            await organizationApiService.addOrganization(newOrganization);
            snackbar.success(t('editOrg.toast.created', { name: organizationName }));
        }
        else if (!compareObjects(newOrganization, currentOrganization)) {
            newOrganization.ogId = currentOrganization.ogId;

            await organizationApiService.updateOrganization(newOrganization);
            snackbar.success(t('editOrg.toast.updated', { name: currentOrganization.ogName }));
        }

        await onSave();
    });

    function getUpdatedOrganization () {
        const formInputs = {
            ogName: organizationName,
            ogDomain: domain,
            ogActive: active,
            ogOtherOrgsPermissionLevel: otherOrgsPermissionLevel,
            ogCanSearch: canSearch,
            ogExternalUser: externalUsers,
            ogCanOnlySeeOwnReported: ownReported,
            ogCanBeAssignedTo: assignedTo,
            ogEnableRiskManagement: riskManagementModule,
            ogProjectFieldPermissionLevel: locationProjectPermission,
            ogOrgFieldPermissionLevel: organizationPermission,
            ogPriorityFieldPermissionLevel: priorityPermission,
            ogStatusFieldPermissionLevel: statusPermission,
            ogAssignedToFieldPermissionLevel: assignedToPermission,
            ogTagsFieldPermissionLevel: tagsPermission,
            ogDueDateFieldPermissionLevel: duePermission,
            ogIsoClauseFieldPermissionLevel: isoClausePermission,
            ogAuditorFieldPermissionLevel: auditorPermission,
            ogDeptFieldPermissionLevel: deptPermission,
            ogProcessFieldPermissionLevel: processPermission,
            ogJobFieldPermissionLevel: jobPermission,
            ogTaskFieldPermissionLevel: taskPermission,
            ogIncidentDescriptionFieldPermissionLevel: incidentDescriptionPermission,
            ogRequirementFieldPermissionLevel: requirementPermission,
            ogFindingFieldPermissionLevel: findingPermission,
            ogEquipmentFieldPermissionLevel: equipmentPermission,
            ogRiskSourceDescriptionFieldPermissionLevel: riskSourceDescriptionPermission,
            ogProposedAdditionalControlFieldPermissionLevel: proposedAdditionalControlPermission,
            ogEvidenceFieldPermissionLevel: evidencePermission,
            ogCorrectionFieldPermissionLevel: correctionPermission,
            ogCauseFieldPermissionLevel: causePermission,
            ogCorrectiveActionFieldPermissionLevel: correctiveActionPermission,
            ogVerifierFieldPermissionLevel: verifierPermission,
            ogVerificationEvidenceFieldPermissionLevel: verificationEvidencePermission,
            ogVerificationDateFieldPermissionLevel: verificationDatePermission,
            ogCanDeleteBug: deleteTasks,
            ogCanEditAndDeletePosts: editDeletePosts,
            ogCanMergeBugs: mergeTasks,
            ogCanMassEditBugs: massEditTasks,
            ogCanUseReports: useReports,
            ogCanEditReports: createEditReports,
            ogCanViewTasks: viewTasksTime,
            ogCanEditTasks: editTasksTime,
            ogCanAssignToInternalUsers: assignInternal,
        };

        return { ...{}, ...formInputs };
    }

    return (
        <Dialog
            sx={{ '& .MuiDialog-paper': { width: '175%' } }}
            maxWidth="lg"
            fullWidth
            open={open}>
            {currentOrganization
                ? (
                    <DialogTitle>{t('editOrg.title.edit', { name: currentOrganization.ogName })}</DialogTitle>)
                : (<DialogTitle>{t('editOrg.title.create')}</DialogTitle>)}
            <DialogContent dividers>
                <Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormLabel htmlFor="orgName">{t('editOrg.field.name')}</FormLabel>
                                <Input
                                    type="text"
                                    value={organizationName}
                                    onChange={(event) => {
                                        setOrganizationName(event.target.value);
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormLabel htmlFor="domain">{t('editOrg.field.domain')}</FormLabel>
                                <Input
                                    type="text"
                                    value={domain}
                                    onChange={(event) => {
                                        setDomain(event.target.value);
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={active === 1}
                                                   onChange={() => setActive(active === 0 ? 1 : 0)}
                                />}
                                label={t('editOrg.field.active')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel>{t('editOrg.field.otherOrgsQuestion')}</FormLabel>
                            <RadioGroup
                                row
                                onChange={(event) => {
                                    setOtherOrgsPermissionLevel(event.target.value);
                                }}
                                value={otherOrgsPermissionLevel}
                            >
                                <FormControlLabel value={0} control={<Radio/>} label={t('permissionLevel.none')}/>
                                <FormControlLabel value={1} control={<Radio/>} label={t('permissionLevel.viewOnly')}/>
                                <FormControlLabel value={2} control={<Radio/>} label={t('permissionLevel.edit')}/>
                            </RadioGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <div>
                                <FormControlLabel
                                    control={<Checkbox checked={canSearch === 1}
                                                       onChange={() => setCanSearch(canSearch === 0 ? 1 : 0)}
                                    />}
                                    label={t('editOrg.field.canSearch')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={<Checkbox checked={externalUsers === 1}
                                                       onChange={() => setExternalUsers(externalUsers === 0 ? 1 : 0)}
                                    />}
                                    label={t('editOrg.field.externalUsersCanView')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={<Checkbox checked={ownReported === 1}
                                                       onChange={() => setOwnReported(ownReported === 0 ? 1 : 0)}
                                    />}
                                    label={t('editOrg.field.canSeeOnlyOwnReported')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={<Checkbox checked={assignedTo === 1}
                                                       onChange={() => setAssignedTo(assignedTo === 0 ? 1 : 0)}
                                    />}
                                    label={t('editOrg.field.assignedToDropdown')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={<Checkbox checked={riskManagementModule}
                                                       onChange={() => setRiskManagementModule(!riskManagementModule)}
                                    />}
                                    label={t('editOrg.field.canAccessRiskModule')}
                                />
                            </div>
                            <Typography variant="body2" color="textSecondary">
                                {t('editOrg.help.riskModule')}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                {t('editOrg.section.fieldLevelPermissions')}
                            </Typography>
                            <Box sx={{
                                height: '50vh',
                                width: '100%',
                                display: 'block',
                            }}>
                                <DataGridPro
                                    columns={columns}
                                    rows={rows}
                                    getRowId={(row) => row.rowId}
                                    onCellEditCommit={(params) => {
                                        rows.find((row) => row.rowId === params.id)?.setPermissionValue(params.value);
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                {t('editOrg.section.nonAdminPermissions')}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={deleteTasks === 1}
                                            onChange={() => setDeleteTasks(deleteTasks === 0 ? 1 : 0)}
                                        />
                                    }
                                    label={t('editOrg.permission.deleteTasks')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={editDeletePosts === 1}
                                            onChange={() => setEditDeletePosts(editDeletePosts === 0 ? 1 : 0)}
                                        />
                                    }
                                    label={t('editOrg.permission.editDeletePosts')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mergeTasks === 1}
                                            onChange={() => setMergeTasks(mergeTasks === 0 ? 1 : 0)}
                                        />
                                    }
                                    label={t('editOrg.permission.mergeTasks')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={massEditTasks === 1}
                                            onChange={() => setMassEditTasks(massEditTasks === 0 ? 1 : 0)}
                                        />
                                    }
                                    label={t('editOrg.permission.massEditTasks')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={useReports === 1}
                                            onChange={() => setUseReports(useReports === 0 ? 1 : 0)}
                                        />
                                    }
                                    label={t('editOrg.permission.useReports')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={createEditReports === 1}
                                            onChange={() => setCreateEditReports(createEditReports === 0 ? 1 : 0)}
                                        />
                                    }
                                    label={t('editOrg.permission.createEditReports')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={viewTasksTime === 1}
                                            onChange={() => setViewTasksTime(viewTasksTime === 0 ? 1 : 0)}
                                        />
                                    }
                                    label={t('editOrg.permission.viewTasksTime')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={editTasksTime === 1}
                                            onChange={() => setEditTasksTime(editTasksTime === 0 ? 1 : 0)}
                                        />
                                    }
                                    label={t('editOrg.permission.editTasksTime')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={assignInternal === 1}
                                            onChange={() => setAssignInternal(assignInternal === 0 ? 1 : 0)}
                                        />
                                    }
                                    label={t('editOrg.permission.assignInternal')}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button autoFocus variant="contained" onClick={handleCancel} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <SaveButton loading={saving} variant="contained" onClick={handleSave}>{t('common.save')}</SaveButton>
            </DialogActions>
        </Dialog>
    );
}
